import { useCallback, useEffect, useLayoutEffect, useReducer } from "react";
import { get } from "lodash";
import { useSelector } from "react-redux";

import { formSelectors } from "store/selectors";

import Helper from "../helper";
import { FORM_EVENTS } from "./useFormSave";


const ELEMENT_TYPES = {
	RICH_TEXT: "richText",
	UPLOAD_INPUT: "uploadInput",
  UPLOAD_REMOVE_BUTTON: "uploadRemoveButton",
}

const getFieldPathByType = (event, type, formConfig) => {
	// get the root element which contains the id
	let fieldPath = "";
	if (type === ELEMENT_TYPES.RICH_TEXT) {
		fieldPath = event.currentTarget.parentElement.parentElement.parentElement.parentElement.parentElement.id;
	} else if (type === ELEMENT_TYPES.UPLOAD_INPUT) {
		fieldPath = event.currentTarget.parentElement.parentElement.parentElement.parentElement.id;
	} else if (type === ELEMENT_TYPES.UPLOAD_REMOVE_BUTTON) {
		fieldPath = event.currentTarget.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement.id;
	}

	// remove redundant part of path
	fieldPath = fieldPath.replace(".extraSettings.layoutDefinition.", "");

	// get field config
	const field = get(formConfig, fieldPath);
	if (!field) {
		throw new Error("useFormCustomFocus -> useEffect -> onFocus: field is undefined");
	}

	const $path = field.value?.replace(/{{|}}/g, "");
	if (!$path) {
		throw new Error("useFormCustomFocus -> useEffect -> onFocus: field path is undefined");
	}

	return $path;
};

// should be removed after add own rich-text editor and input upload type.
export const useFormCustomFocus = (isFormReady, formContainerRef) => {
	const { formConfig } = useSelector(formSelectors.entity);

	const [ updated, forceUpdate ] = useReducer(prev => prev + 1, 0);

	useLayoutEffect(() => {
		if (!isFormReady || !formContainerRef.current) return;

		const sectionsContainer = formContainerRef.current.getElementsByClassName("flw__tabs__tabs-list")[0];
		
		if(sectionsContainer) {
			// trigger force update to find and assign events
			sectionsContainer.addEventListener("click", forceUpdate);

			return () => {
				sectionsContainer.removeEventListener("click", forceUpdate);
			};
		}	

	}, [ isFormReady ]);

	const onFocusFlowableRichText = useCallback(event => {
		const $path = getFieldPathByType(event, ELEMENT_TYPES.RICH_TEXT, formConfig);

		// dispatch custom focus event
		Helper.formRef.onEvent(FORM_EVENTS.CUSTOM_MANUAL_FOCUS, { $path });

	}, [ formConfig ]);

	const onChangeUpload = useCallback((type) => (event) => {
		const $path = getFieldPathByType(event, type, formConfig);

		Helper.formRef.onEvent(FORM_EVENTS.CUSTOM_MANUAL_FOCUS, { $path });

		// need to use timeout because of async rendering after change section
		setTimeout(() => {
			// reassign events to handle pressing the 'delete' button when adding a new file
			subscribeUploadElements();
		}, 50);

	}, [ formConfig ]);

	const subscribeRichTextElements = useCallback(() => {
		// subscribe rich-text elements
		formContainerRef.current.querySelectorAll(".DraftEditor-editorContainer")
			.forEach((el) => {
				// use onclick and onkeydown to handle "focus",
				// because focus can be lost after a conflict,
				// and data will be lost (only for input. present in form data)
				el.children[0].onclick = onFocusFlowableRichText;
				el.children[0].onkeydown = onFocusFlowableRichText;
			});

	}, [ onFocusFlowableRichText ]);

	const subscribeUploadElements = useCallback(() => {
		// subscribe inputs
		formContainerRef.current.querySelectorAll("input[type='file']")
			.forEach((el) => {
				el.onchange = onChangeUpload(ELEMENT_TYPES.UPLOAD_INPUT);
			});

		// subscribe remove file buttons
		formContainerRef.current.querySelectorAll(".flw__fileCard__fileAction")
			.forEach((el) => {
				el.onclick = onChangeUpload(ELEMENT_TYPES.UPLOAD_REMOVE_BUTTON);
			});

	}, [ onChangeUpload ]);

	useEffect(() => {
		if (!isFormReady || !formContainerRef.current) return;

		// need to use timeout because of async rendering after change section
		setTimeout(() => {
			subscribeRichTextElements();
			//subscribeUploadElements();
		}, 50);

	}, [ updated ]);

};
